import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, ArrowRight } from "lucide-react";

const TIERS = {
  gold:   { label: "Gold",   cls: "text-amber-700  bg-amber-50  border-amber-200"   },
  silver: { label: "Silver", cls: "text-slate-700  bg-slate-50  border-slate-200"  },
  bronze: { label: "Bronze", cls: "text-orange-700 bg-orange-50 border-orange-200" },
  watch:  { label: "Watch",  cls: "text-rose-700   bg-rose-50   border-rose-200"   },
  new:    { label: "Warming up", cls: "text-slate-600 bg-slate-50 border-slate-200" },
};

export default function PublisherLeaderboard() {
  const [q, setQ] = useState(null);
  const [optIn, setOptIn] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [qr, or] = await Promise.all([
          api.get("/publisher/quality-score"),
          api.get("/publisher/leaderboard-optin"),
        ]);
        setQ(qr.data);
        setOptIn(!!or.data?.opt_in);
      } catch (e) {
        toast.error("Failed to load quality score");
      }
    })();
  }, []);

  const toggleOptIn = async () => {
    setSaving(true);
    try {
      const next = !optIn;
      await api.post(`/publisher/leaderboard-optin?opt_in=${next}`);
      setOptIn(next);
      toast.success(next ? "Listed on public leaderboard" : "Removed from public leaderboard");
    } catch (e) {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  const tier = TIERS[q?.tier] || TIERS.new;

  return (
    <div className="space-y-6" data-testid="publisher-leaderboard-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Leaderboard & quality score</h1>
        <p className="text-[13px] text-slate-500 mt-1">Your rolling 30-day quality score and public listing on connex.is/publishers/leaderboard.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Quality score */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-soft p-6" data-testid="pub-quality-card">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">Quality score</h2>
            <span className={`text-[11px] px-2 py-0.5 rounded-full border ${tier.cls}`} data-testid="pub-quality-tier">{tier.label}</span>
          </div>
          <p className="text-[12.5px] text-slate-500 mt-1">Accept rate × 60% + kept rate × 40%, computed on a 30-day rolling window.</p>
          <div className="mt-5">
            {q?.score == null ? (
              <div className="text-[13px] text-slate-600">
                Need <span className="font-semibold tabular-nums">{Math.max(0, 20 - (q?.sample_size || 0))}</span> more leads to score (min 20 in window).
              </div>
            ) : (
              <>
                <div className="flex items-baseline gap-2">
                  <div className="text-5xl font-semibold text-slate-900 tabular-nums">{q.score.toFixed(1)}</div>
                  <div className="text-[12px] text-slate-500">/ 100</div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2 text-[12px]">
                  <div className="rounded-lg bg-slate-50 px-3 py-2">
                    <div className="text-slate-500 text-[11px]">Accept rate</div>
                    <div className="font-mono font-medium text-slate-900">{((q.accept_rate || 0) * 100).toFixed(1)}%</div>
                  </div>
                  <div className="rounded-lg bg-slate-50 px-3 py-2">
                    <div className="text-slate-500 text-[11px]">Kept rate</div>
                    <div className="font-mono font-medium text-slate-900">{((q.kept_rate || 0) * 100).toFixed(1)}%</div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Public leaderboard opt-in */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-soft p-6">
          <div className="flex items-center gap-2">
            <span className="inline-grid place-items-center size-8 rounded-md bg-amber-500 text-white"><Trophy className="size-4" strokeWidth={1.7} /></span>
            <h2 className="text-lg font-semibold text-slate-900">Public leaderboard</h2>
          </div>
          <p className="text-[12.5px] text-slate-500 mt-2">Top-ranked publishers earn placement on the public leaderboard at <Link to="/publishers/leaderboard" className="text-[#0055FF] hover:underline">connex.is/publishers/leaderboard</Link>. Visible to prospective buyers — useful as social proof.</p>

          <div className="mt-5 flex items-center justify-between gap-3 pt-4 border-t border-slate-100">
            <div className="min-w-0">
              <div className="text-[13px] font-medium text-slate-900">Show my company on the public leaderboard</div>
              <div className="text-[11px] text-slate-500">You can revoke at any time. Only ranking position + display name is published — no PII.</div>
            </div>
            <button
              onClick={toggleOptIn}
              disabled={saving}
              className={`shrink-0 inline-flex h-6 w-11 items-center rounded-full transition-colors ${optIn ? "bg-[#0055FF]" : "bg-slate-200"}`}
              data-testid="pub-leaderboard-toggle"
              aria-pressed={optIn}
            >
              <span className={`inline-block size-5 rounded-full bg-white shadow transform transition-transform ${optIn ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
          </div>

          <div className="mt-5">
            <Link to="/publishers/leaderboard" className="text-[12.5px] text-[#0055FF] font-medium hover:underline inline-flex items-center gap-1">
              View public leaderboard <ArrowRight className="size-3" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
